import { useFetchApartments } from "../../../redux/flows/fetchApartmentsFlow";
import { ServiceTypeSelectorOption } from "../../../types/order";
import BookingFlowShell, {
  BookingFlowShellConfig,
} from "../common/components/BookingFlowShell/BookingFlowShell";
import ApartmentOverview from "./ApartmentOverview";
import ApartmentSelectionStep from "./ApartmentSelectionStep";
import BookingOverview from "./BookingOverview";
import LocationAndDurationSelectionStep from "./LocationAndDurationSelectionStep";
import Payment from "./Payment";
import PersonalData from "./PersonalDataStep";
import BookingSuccess from "../venueReservation/BookingSuccess";
import { BookingCreationStep } from "../common/bookingCreationStep";

// Re-exported for the flow's existing importers; defined in its own module
// so consuming the enum does not drag this page in.
export { BookingCreationStep };

/**
 * Room rental, on the shared booking shell (ADR-0026, phase 1 step 5).
 *
 * Everything that used to be ~400 lines of page is now the config below plus
 * this flow's own step components. What is NOT obvious and is deliberate:
 *
 * - `paidReservationUseCase` is `undefined`, not `"apartment"`.
 *   `createReservationFlow` puts this value on the request body and branches
 *   on whether it is booked by the hour, so passing the flow's own name here
 *   would change what a payment request sends. It has always been absent; it stays absent.
 * - `updateServiceOn: "summary"` — this flow writes the selected service on
 *   the way into the summary, where venue does it on mount.
 * - `passesUpdateHandlerInRequestFlow` — this flow hands the footer its update
 *   handler in both flows; venue only under direct booking.
 */
const config: BookingFlowShellConfig = {
  directBookingKey: "apartment",
  serviceType: ServiceTypeSelectorOption.APARTMENT,
  updateServiceOn: "summary",
  stepWrapperId: "apartmentProcessStepWrapper",
  paidReservationUseCase: undefined,
  requestOrderUseCase: "apartment",
  appliesVoucher: true,
  passesUpdateHandlerInRequestFlow: true,
  headerKeys: {
    overview: "book_location.apartment_overview.header_text",
    personalData: "book_location.apartment_overview.personal_data",
  },
  objectsFoundKey: "apartment.apartments_found",
  searchErrorMessageKey: "book_location.error.fetch_apartments",
};

interface BookLocationDialogProps {
  returnPage: string;
}

const BookLocationPage = (props: BookLocationDialogProps) => {
  const fetchApartments = useFetchApartments();

  return (
    <BookingFlowShell
      config={config}
      fetchObjects={fetchApartments}
      steps={{
        locationSelection: <LocationAndDurationSelectionStep />,
        objectSelection: <ApartmentSelectionStep />,
        objectOverview: <ApartmentOverview />,
        bookingSummary: <BookingOverview />,
        personalData: <PersonalData />,
        payment: <Payment />,
        success: <BookingSuccess />,
      }}
    />
  );
};

export default BookLocationPage;
